import React, { memo, useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter, { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import FormatCurrency from 'components/Common/FormatCurrency'
import Divider from 'LuxKit/Divider'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPriceDetails from 'LuxKit/PricePoints/PriceRowPriceDetails'

interface Props {
  experience?: App.ExperienceOffer;
  ticket?: App.ExperienceBookingTicketView;
  ticketCount?: number;
  /**
   * @default horizontal
   */
  orientation?: 'horizontal' | 'vertical';
}

const Container = styled.div`
  width: max-content;
`

const StyledGroup = styled(Group)`
  padding-right: ${rem(8)};
`

function ExperiencePriceDetails(props: Props) {
  const { pathname } = useLocation()
  const { on: open, off: close, value: isOpen } = useToggle()
  const [expanded, setExpanded] = useState<boolean>(false)

  const { experience, ticket, ticketCount, orientation = 'horizontal' } = props

  const expand = () => setExpanded(!expanded)

  const prices = useMemo(() => {
    let price = 0
    let taxesAndFees = 0
    let priceBeforeTax = 0

    if (ticket?.taxesAndFees) {
      taxesAndFees = ticketCount ? (ticket.taxesAndFees * ticketCount) : ticket.taxesAndFees
      price = ticketCount ? (ticket.price * ticketCount) : ticket.price
      priceBeforeTax = price - taxesAndFees
    }

    if (experience?.taxesAndFees) {
      taxesAndFees = experience.taxesAndFees
      price = experience.price // price is already coming with the taxesAndFees included when the offers has taxesAndFees
      priceBeforeTax = experience.price - experience.taxesAndFees
    }

    return { price, priceBeforeTax, taxesAndFees }
  }, [experience, ticket, ticketCount])

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.stopPropagation()
    e.preventDefault()
    open()
  }, [open])

  const showFooter = useMemo(() => experience && !pathname.includes(experience?.id), [experience, pathname])

  const modalFooterSecondaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    children: (
      <TextButton kind="primary" fit="flex" to={`/experience/${experience?.slug}/${experience?.id}`}>
        View Offer
      </TextButton>
    ),
    onClick: close,
    as: 'div',
    kind: 'secondary',
  }), [close, experience])

  return <Container>
    <Group direction={orientation}>
      <PriceRowTaxesAndFees
        endIcon={(!!prices.taxesAndFees && orientation === 'horizontal') && <Clickable onClick={handleClick}>
          <LineInfoCircleIcon />
        </Clickable>}
      />
      {(!!prices.taxesAndFees && orientation === 'vertical') && <PriceRowPriceDetails
        size="M"
        onClick={handleClick}
      />}
    </Group>
    <ModalBase
      size="L"
      isOpen={isOpen}
      onClose={close}
    >
      <ModalHeader title="Price details" onCloseButtonClick={close} />
      <ModalBody>
        <ModalContent>
          <StyledGroup direction="vertical" gap={12}>
            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">
                {experience?.name ?? ticket?.name}
              </BodyText>
              <BodyText as="span" variant="medium">
                <FormatCurrency value={prices.priceBeforeTax} />
              </BodyText>{' '}
            </Group>

            <Divider kind="secondary" />

            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">
                Taxes and fees
              </BodyText>
              <BodyText variant="medium">
                <FormatCurrency value={prices.taxesAndFees} />
              </BodyText>
            </Group>
            {expanded && <BodyText as="p" variant="medium">
              This charge includes estimated amounts the service provider pays for their taxes and fees and/or taxes and fees that we pay to authorities on your booking
              (including but not limited to sales, environmental, entertainment, fuel, city, state and value-added tax) – see our <TextLink target="_blank" to="/terms-and-conditions" underline>Terms and Conditions</TextLink> here.
              Please refer to the Fine Print for any additional costs payable directly to the provider.
            </BodyText>}
            <SmallToggleButton onClick={expand} on={expanded} size="small">
              {expanded ? 'Read Less' : 'Learn More'}
            </SmallToggleButton>

            <Divider kind="secondary" />

            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="large" weight="bold">
                Total
              </BodyText>
              <BodyText variant="large" weight="bold">
                <FormatCurrency value={prices.price} />
              </BodyText>
            </Group>
          </StyledGroup>
        </ModalContent>
      </ModalBody>
      {showFooter && <ModalFooter secondaryActionProps={modalFooterSecondaryActionProps} />}
    </ModalBase>

  </Container>
}

ExperiencePriceDetails.displayName = 'ExperiencePriceDetails'

export default memo(ExperiencePriceDetails)
